/**
 * Masking for identity values shown on screen.
 *
 * A scan result is reviewed at a counter, in front of the person it belongs to and often in front of
 * whoever is next in the queue. The operator needs enough to confirm the read went to the right
 * record — the last few characters do that — and nobody needs a full national identification number
 * legible from across a shop.
 *
 * This is a display rule, not a security boundary: the value is in the page either way. What it
 * changes is what is readable over a shoulder, and what ends up in a screenshot pasted into a
 * support ticket.
 */

/** How many characters stay legible at the end of a masked value. */
export const VISIBLE_SUFFIX_LENGTH = 4;

/**
 * Masks all but the last few characters.
 *
 * A value short enough that a suffix would give away most of it is masked completely: showing four
 * of five characters is not masking, it is a formatting change.
 */
export function maskIdentityValue(value: string, visible: number = VISIBLE_SUFFIX_LENGTH): string {
    const trimmed = value.trim();

    if (trimmed.length === 0) {
        return '';
    }

    if (trimmed.length <= visible + 1) {
        return '•'.repeat(trimmed.length);
    }

    return `${'•'.repeat(trimmed.length - visible)}${trimmed.slice(-visible)}`;
}

/**
 * Whether a field is one to mask by default.
 *
 * Names and dates are not: an operator confirming they scanned the right person's document has to be
 * able to read the name, and a masked date of birth would make the review useless. The identifiers
 * are the ones worth hiding, because they are the ones worth copying.
 */
export function isSensitiveIdentityField(field: string): boolean {
    return field === 'cnp' || field === 'personalNumber' || field === 'documentNumber';
}
